/**
 * React hook for offline sync functionality
 * Provides sync status and methods for components
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  initSyncManager,
  destroySyncManager,
  triggerSync,
  getStatus,
  subscribe,
  retryTransaction,
  skipTransaction
} from '../utils/offlineSync.js';
import {
  initOfflineDb,
  addPendingTransaction,
  getAllTransactions,
  getUnresolvedConflicts,
  hasPendingTransactionForTable,
  countTransactionsByStatus
} from '../utils/offlineDb.js';
import { generateOfflineTransactionId } from '../utils/generateOfflineId.js';

/**
 * Hook for managing offline sync state and operations
 * @param {string} apiUrl - The API base URL
 * @param {string} [branchId] - Currently selected branch (effectiveBranchId from useBranch()).
 *   IndexedDB is per-browser, not per-branch, so on a device/account that can see more than
 *   one branch (owner switching the branch selector), pending transactions and conflicts from
 *   every branch used on that browser would otherwise show up mixed together regardless of
 *   which branch is currently selected. When provided, every list/count here is scoped to it.
 */
export function useOfflineSync(apiUrl, branchId) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [status, setStatus] = useState({
    isOnline: true,
    isSyncing: false,
    pending: 0,
    syncing: 0,
    synced: 0,
    conflicts: 0,
    failed: 0,
    needsReview: 0,
    total: 0
  });
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const initRef = useRef(false);

  // Read via ref (not the closed-over `branchId` value) so the subscribe
  // callbacks below — registered once — always see the latest selected branch,
  // e.g. when the owner flips the branch switcher without remounting this page.
  const branchIdRef = useRef(branchId);
  branchIdRef.current = branchId;

  const filterByBranch = (txns) => {
    const b = branchIdRef.current;
    return b ? txns.filter(t => t.branchId === b) : txns;
  };

  const filterConflictsByBranch = (conflictList) => {
    const b = branchIdRef.current;
    return b ? conflictList.filter(c => c.transaction?.branchId === b) : conflictList;
  };

  // isOnline/isSyncing/lastSyncTime are genuinely global (one sync engine per browser);
  // pending/conflicts/synced/etc. are recomputed from the branch-filtered transaction list
  // so the counts always match what's actually shown for the selected branch.
  const computeBranchStatus = (globalStatus, branchTxns) => {
    const counts = countTransactionsByStatus(branchTxns);
    return {
      ...globalStatus,
      pending: counts.pending,
      syncing: counts.syncing,
      synced: counts.synced,
      conflicts: counts.conflict,
      failed: counts.failed,
      needsReview: counts.needsReview,
      total: counts.total
    };
  };

  // Initialize the sync system
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    async function init() {
      try {
        // Initialize IndexedDB first
        await initOfflineDb();

        // Initialize sync manager
        await initSyncManager(apiUrl);

        // Get initial status
        const initialStatus = await getStatus();
        const txns = await getAllTransactions();
        const branchTxns = filterByBranch(txns);
        setStatus(computeBranchStatus(initialStatus, branchTxns));

        // Load transactions and conflicts
        setPendingTransactions(branchTxns.filter(t => t.syncStatus !== 'synced'));

        const unresolvedConflicts = await getUnresolvedConflicts();
        setConflicts(filterConflictsByBranch(unresolvedConflicts));

        setIsInitialized(true);
        console.log('[useOfflineSync] Initialized');
      } catch (error) {
        console.error('[useOfflineSync] Failed to initialize:', error);
      }
    }

    init();

    return () => {
      destroySyncManager();
    };
  }, [apiUrl]);

  // Subscribe to sync events
  useEffect(() => {
    if (!isInitialized) return;

    const unsubscribers = [];

    // Status change
    unsubscribers.push(
      subscribe('status_change', async (newStatus) => {
        // Refresh transaction list
        const txns = await getAllTransactions();
        const branchTxns = filterByBranch(txns);
        setStatus(computeBranchStatus(newStatus, branchTxns));
        setPendingTransactions(branchTxns.filter(t => t.syncStatus !== 'synced'));
      })
    );

    // Conflict detected
    unsubscribers.push(
      subscribe('conflict', async () => {
        const unresolvedConflicts = await getUnresolvedConflicts();
        setConflicts(filterConflictsByBranch(unresolvedConflicts));
      })
    );

    // Conflict auto-resolved
    unsubscribers.push(
      subscribe('conflict_auto_resolved', async () => {
        const unresolvedConflicts = await getUnresolvedConflicts();
        setConflicts(filterConflictsByBranch(unresolvedConflicts));
      })
    );

    return () => {
      unsubscribers.forEach(unsub => {
        try {
          unsub();
        } catch (e) {
          console.warn('[useOfflineSync] Error unsubscribing:', e);
        }
      });
    };
  }, [isInitialized]);

  /**
   * Add a checkout transaction to the offline queue
   */
  const addCheckout = useCallback(async (checkoutData) => {
    const offlineId = generateOfflineTransactionId();

    const transaction = {
      offlineId,
      ...checkoutData,
      createdAt: Date.now()
    };

    await addPendingTransaction(transaction);

    // Refresh status + transactions list
    const newStatus = await getStatus();
    const txns = await getAllTransactions();
    const branchTxns = filterByBranch(txns);
    setStatus(computeBranchStatus(newStatus, branchTxns));
    setPendingTransactions(branchTxns.filter(t => t.syncStatus !== 'synced'));

    // ตั้งใจไม่ triggerSync() ที่นี่ — Checkout.jsx ยิง POST ตรงไป /api/transactions/checkout
    // เองทันทีถ้า isOnline อยู่แล้ว ถ้ายิง sync พร้อมกันตรงนี้ด้วย จะกลายเป็นสอง request
    // แข่งกันเขียนบิลเดียวกันลง DB (เจอเป็นบิลซ้ำจริงที่หน้าร้าน 2026-09-02 — สอง request
    // ถึง MongoDB ไล่เลี่ยกันเร็วกว่าที่ unique-index กันซ้ำจะทันเห็น) ปล่อยให้ periodic
    // sync (ทุก 30s) กับ handleOnline() ตอนเน็ตกลับมาเป็นคนคอยเก็บ record ที่ค้าง pending
    // จริง ๆ (offline ตอนสร้าง) แทน — กรณีนั้น newStatus.isOnline เป็น false อยู่แล้วตอน
    // เรียกจากตรงนี้ ไม่เคยเข้าเงื่อนไขนี้ตั้งแต่ต้น เอาออกจึงไม่กระทบเคส offline จริง

    return offlineId;
  }, []);

  /**
   * Check if a table already has a pending checkout
   */
  const checkTablePending = useCallback(async (tableCode) => {
    return await hasPendingTransactionForTable(tableCode);
  }, []);

  /**
   * Retry a failed transaction
   */
  const retry = useCallback(async (offlineId) => {
    await retryTransaction(offlineId);
  }, []);

  /**
   * Skip a transaction (mark as resolved)
   */
  const skip = useCallback(async (offlineId, reason) => {
    await skipTransaction(offlineId, reason);

    // Refresh status + transactions
    const newStatus = await getStatus();
    const txns = await getAllTransactions();
    const branchTxns = filterByBranch(txns);
    setStatus(computeBranchStatus(newStatus, branchTxns));
    setPendingTransactions(branchTxns.filter(t => t.syncStatus !== 'synced'));
  }, []);

  /**
   * Force trigger a sync
   */
  const sync = useCallback(() => {
    triggerSync();
  }, []);

  /**
   * Refresh the status manually
   */
  const refreshStatus = useCallback(async () => {
    const newStatus = await getStatus();
    const txns = await getAllTransactions();
    const branchTxns = filterByBranch(txns);
    setStatus(computeBranchStatus(newStatus, branchTxns));
    setPendingTransactions(branchTxns.filter(t => t.syncStatus !== 'synced'));

    const unresolvedConflicts = await getUnresolvedConflicts();
    setConflicts(filterConflictsByBranch(unresolvedConflicts));
  }, []);

  // Re-scope the displayed lists/counts immediately when the selected branch changes
  // (e.g. owner switches branch via BranchSwitcher) instead of waiting for the next
  // sync event or the 30s poll.
  useEffect(() => {
    if (!isInitialized) return;
    refreshStatus();
  }, [branchId, isInitialized, refreshStatus]);

  return {
    isInitialized,
    status,
    pendingTransactions,
    conflicts,
    addCheckout,
    checkTablePending,
    retry,
    skip,
    sync,
    refreshStatus,
    // Convenience getters
    isOnline: status.isOnline,
    isSyncing: status.isSyncing,
    hasPending: status.pending > 0,
    hasConflicts: status.conflicts > 0
  };
}

/**
 * Simple hook just for online/offline status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

export default useOfflineSync;
